import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB

export default function ChunkedUploader({ courseId, webinarId, defaultOrder = 0, onUploaded }) {
  const { API } = useAuth();
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(""); // idle | uploading | assembling | done | error
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a video file");
    if (!title) return toast.error("Enter a title");
    setUploading(true);
    setStatus("uploading");
    setProgress(0);

    try {
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const init = await axios.post(`${API}/videos/chunked/init`, {
        course_id: courseId, webinar_id: webinarId,
        title, order: defaultOrder,
        filename: file.name, content_type: file.type || "video/mp4",
        total_chunks: totalChunks, total_size: file.size,
      }, { withCredentials: true });
      const uploadId = init.data.upload_id;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const chunk = file.slice(start, start + CHUNK_SIZE);
        const fd = new FormData();
        fd.append("file", chunk, `chunk_${i}`);
        await axios.post(`${API}/videos/chunked/${uploadId}/${i}`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      setStatus("assembling");
      const fin = await axios.post(`${API}/videos/chunked/${uploadId}/finish`, {}, { withCredentials: true });
      setStatus("done");
      toast.success("Video uploaded");
      setTitle("");
      setFile(null);
      setProgress(0);
      onUploaded?.(fin.data);
    } catch (err) {
      setStatus("error");
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={upload} className="space-y-3" data-testid="chunked-upload-form">
      <div className="grid md:grid-cols-3 gap-3 items-end">
        <label className="md:col-span-1">
          <div className="overline mb-1">Title</div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input"
                 data-testid="chunk-video-title-input" />
        </label>
        <label className="md:col-span-1">
          <div className="overline mb-1">Video file</div>
          <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files[0])}
                 className="text-sm" data-testid="chunk-video-file-input" />
        </label>
        <button disabled={uploading} className="btn-primary" data-testid="chunk-upload-submit">
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      {uploading && (
        <div data-testid="upload-progress-container">
          <div className="h-2 rounded-full bg-[#E5E3D8] overflow-hidden">
            <div className="h-full bg-[#0A231C] transition-all" style={{ width: `${progress}%` }} data-testid="upload-progress-bar" />
          </div>
          <div className="mt-1 text-xs text-[#5C5C5C]">
            {status === "uploading" ? `Uploading ${progress}%` : status === "assembling" ? "Assembling on server…" : status}
          </div>
        </div>
      )}
      {file && !uploading && (
        <div className="text-xs text-[#5C5C5C]">Selected: {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB · {Math.ceil(file.size / CHUNK_SIZE)} chunks</div>
      )}
      <style>{`.input{width:100%;padding:0.6rem 0.85rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit;font-size:0.9rem}.input:focus{border-color:#0A231C}`}</style>
    </form>
  );
}
